import { isPlatformBrowser, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { MediaQuery, MediaQueryChangeEvent } from '@workbench/core';
import { catchError, of, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { AssetType } from '@modules/cards/types/asset.types';
import { ENTITY_SORT_OPTIONS } from '@modules/shared/configs/sort-options';
import { SortOptionInterface } from '@modules/shared/interfaces/sort-option.interface';
import { TagMenu } from '@modules/shared/schemas/tag.schema';
import { FilterService } from '@modules/shared/services/filter.service';
import { MediaQueryService } from '@modules/shared/services/media-query.service';
import { SearchOptions, SearchService } from '@modules/shared/services/search.service';

import { EventCardComponent } from '../../cards/event-card/event-card.component';
import { MediaCardComponent } from '../../cards/media-card/media-card.component';

import { FiltertoolNarrowComponent } from './components/filtertool-narrow/filtertool-narrow.component';
import { SearchResult } from './search-result.model';

const GRID_CONFIG: { [mq in MediaQuery]: number } = {
  mq1: 4,
  mq2: 4,
  mq3: 6,
  mq4: 6,
  mq5: 6,
  mq6: 8,
};

@Component({
  selector: 'mb-dynamic-section',
  templateUrl: './dynamic-section.component.html',
  styleUrls: ['./dynamic-section.component.scss'],
  providers: [FilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FiltertoolNarrowComponent, FormsModule, EventCardComponent, MediaCardComponent, SlicePipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DynamicSectionComponent implements OnInit, OnDestroy {
  private searchService = inject(SearchService);
  private cd = inject(ChangeDetectorRef);
  private mediaQueryService = inject(MediaQueryService);
  private filterService = inject(FilterService);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  public readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @Input() topicId?: string;
  @Input() title?: string;
  @Input() entities: AssetType[] = [];
  @Input() filterGroups: number[] = [];
  @Input() assetsCount: { [k in AssetType]?: number } = {};
  @Input() showSingleTabNav: boolean = true;
  @Input() showShowAllButton: boolean = true;
  @Input() entityLabelOverrides: Partial<{ [k in AssetType]: string }> = {};

  public entitiesWithContent: AssetType[] = [];
  public cards: SearchResult[] = [];
  public assetCount = 0;
  public savedFilteredTags: { [k in AssetType]?: TagMenu[] } = {
    DOCUMENT: [],
    IMAGE: [],
    PRESS_INFORMATION: [],
    PRESS_KIT: [],
    VIDEO: [],
    AUDIO: [],
    EVENT: [],
    MEDIA_COLLECTION: [],
    MEDIA_SPECIAL: [],
    PRESS_CONTACT: [],
    TECHNICAL_DATA: [],
  };
  public shownCardsCount: number = 0;
  public initialCardAmount: number = 0;
  public searchOptions!: SearchOptions;
  public isError: boolean = false;
  public initialLoadComplete: boolean = false;

  public maxPages = 4;

  public sortOptions: SortOptionInterface[] = [];

  private selectedSortOptions: Partial<{ [k in AssetType]: SortOptionInterface }> = {};

  private searchOptions$ = new Subject<SearchOptions>();
  private destroy$ = new Subject<boolean>();

  @HostListener('window:wbresize', ['$event'])
  public onResize(event: CustomEvent<MediaQueryChangeEvent>) {
    this.initialCardAmount = GRID_CONFIG[event.detail.current];
    this.shownCardsCount = this.initialCardAmount;
  }

  constructor() {
    this.initialCardAmount = GRID_CONFIG[this.mediaQueryService.getCurrentMediaQuery()];
    this.shownCardsCount = this.initialCardAmount;

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cards = [];
      this.searchOptions.page = 0;
      this.searchOptions$.next(this.searchOptions);
    });

    this.searchOptions$
      .pipe(
        switchMap((searchOptions) =>
          this.searchService.search(searchOptions).pipe(
            tap((result) => {
              this.assetsCount = result.meta.countsPerType;
              this.entitiesWithContent = this.entities.filter((e) => !!this.assetsCount[e]);
              this.initialLoadComplete = true;

              if (!searchOptions.page) {
                // reset amount of rendered cards when loading the first page
                this.shownCardsCount = this.initialCardAmount;
              }

              // add the new cards
              this.cards.push(...result.items);

              const activeTags = result.meta.tags;
              this.filterService.changeActiveTags(activeTags, searchOptions.entity);

              const availableTags = result.meta.availableTags;
              this.filterService.changeAvailableTags(availableTags, searchOptions.entity);

              this.assetCount = result.meta.total;

              this.cd.markForCheck();
            }),
            catchError(() => {
              this.isError = true;
              this.initialLoadComplete = true;
              this.cd.markForCheck();
              return of(null);
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.entitiesWithContent = this.entities.filter((e) => !!this.assetsCount[e]);
    this.assetCount = this.assetsCount[this.entitiesWithContent[0]] ?? 0;

    // initial asset counts might not be up to date, just fetch the first configured one
    const initialEnttiy = this.entities[0];

    this.sortOptions = ENTITY_SORT_OPTIONS[initialEnttiy] ?? [];
    this.selectedSortOptions[initialEnttiy] = this.sortOptions[0];

    this.searchOptions = {
      entity: initialEnttiy,
      page: 0,
      topic: this.topicId ? Number(this.topicId) : undefined,
      tags: this.savedFilteredTags[initialEnttiy]?.map((key) => {
        return key.id;
      }),
      sortOption: this.sortOptions[0],
    };

    this.filterService.filteredTags$.pipe(takeUntil(this.destroy$)).subscribe((tags) => {
      this.searchOptions.page = 0;
      this.cards = [];
      this.shownCardsCount = this.initialCardAmount;
      this.savedFilteredTags = tags;
      this.searchOptions.tags = this.savedFilteredTags[this.searchOptions.entity]?.map((key) => {
        return key.id;
      });
      this.searchOptions.page = 0;
      this.searchOptions$.next(this.searchOptions);
    });
  }

  public getEntityLabelKey(e: AssetType): string {
    return this.entityLabelOverrides?.[e] ?? e + 'S';
  }

  public hasEntityLabelOverride(e: AssetType): boolean {
    return !!this.entityLabelOverrides?.[e];
  }

  public getEntityCountKey(e: AssetType, count: number): string {
    const base = this.entityLabelOverrides?.[e] ?? e;
    return count === 1 ? `${base}_N` : `${base}S_N`;
  }

  public openTab(e: AssetType) {
    this.isError = false;
    this.assetCount = this.assetsCount[e] ?? 0;
    this.searchOptions.entity = e;
    this.sortOptions = ENTITY_SORT_OPTIONS[e] ?? [];
    this.searchOptions.sortOption = this.selectedSortOptions[e] ?? this.sortOptions[0];
    this.filterService.set(this.savedFilteredTags[this.searchOptions.entity] ?? [], this.searchOptions.entity);
  }

  public onSortChange(s: SortOptionInterface) {
    this.searchOptions.sortOption = s;
    this.selectedSortOptions[this.searchOptions.entity] = s;
    this.searchOptions.page = 0;
    this.cards = [];
    this.searchOptions$.next(this.searchOptions);
  }

  public showMore() {
    this.shownCardsCount += this.initialCardAmount;
    if (this.shownCardsCount > this.cards.length) {
      this.searchOptions.page!++;
      this.searchOptions$.next(this.searchOptions);
    }
  }

  public showAll() {
    const queryParams: { entity: string; tags?: string } = {
      entity: this.searchOptions.entity,
    };
    if (this.savedFilteredTags[this.searchOptions.entity]?.length) {
      queryParams['tags'] = this.savedFilteredTags[this.searchOptions.entity]?.map((tag) => tag.id).join(',');
    }

    this.router.navigate(
      [
        'content',
        ...this.router.url
          .slice(1)
          .split('/')
          .filter((path) => !!path),
      ],
      {
        queryParams,
      }
    );
  }

  public showTabNavigation() {
    if (!this.initialLoadComplete) {
      return false;
    }

    if (this.entitiesWithContent.length === 0) {
      return false;
    }

    if (this.entitiesWithContent.length === 1 && !this.showSingleTabNav) {
      return false;
    }

    return true;
  }

  public reload() {
    this.isError = false;
    this.searchOptions$.next(this.searchOptions);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();

    this.filterService.resetAll();
  }
}
